/**
 * Portal Orders Page
 * Order management for shop owners
 */

import React, { useEffect, useState } from 'react';
import portalService from '../../services/portal.service';
import api from '../../services/api';

const ESTADOS: Record<string, { label: string; color: string }> = {
  PENDENTE:      { label: 'Pendente',       color: 'bg-yellow-100 text-yellow-800' },
  PAGO:          { label: 'Pago',           color: 'bg-green-100 text-green-800' },
  CONFIRMADO:    { label: 'Confirmado',     color: 'bg-blue-100 text-blue-800' },
  EM_PREPARACAO: { label: 'Em Preparação',  color: 'bg-purple-100 text-purple-800' },
  ENVIADO:       { label: 'Enviado',        color: 'bg-indigo-100 text-indigo-800' },
  ENTREGUE:      { label: 'Entregue',       color: 'bg-green-200 text-green-900' },
  CANCELADO:     { label: 'Cancelado',      color: 'bg-red-100 text-red-800' },
};

const PortalOrders: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [search, setSearch] = useState('');
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [repairRef, setRepairRef] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [repairMsg, setRepairMsg] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await portalService.getOrders({ estado: filtroEstado, search });
      setOrders(res.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filtroEstado]);

  const handleUpdateStatus = async (orderId: string, estado: string) => {
    try {
      await portalService.updateOrderStatus(orderId, estado);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRepair = async () => {
    if (!repairRef.trim()) return;
    if (!window.confirm(`Corrigir encomenda com Ref. SISP "${repairRef.trim()}" e associar à sua loja?`)) return;
    setRepairing(true);
    setRepairMsg(null);
    try {
      const val = repairRef.trim();
      const isEmail = val.includes('@');
      const res = await api.post('/portal/products/orders/repair-by-sisp-ref',
        isEmail ? { refInterna: '', email: val } : { refInterna: val }
      );
      if (res.data?.success) {
        setRepairMsg(res.data.message || 'Encomenda corrigida com sucesso.');
        setRepairRef('');
        loadData();
      } else {
        setRepairMsg(res.data?.error || 'Erro ao corrigir encomenda.');
      }
    } catch (err: any) {
      const errData = err?.response?.data;
      const base = errData?.error || err.message || 'Erro ao corrigir encomenda.';
      const debug = errData?.debug ? ` | Debug: ${JSON.stringify(errData.debug)}` : '';
      setRepairMsg(base + debug);
    } finally {
      setRepairing(false);
    }
  };

  const handleActivateDigital = async (orderId: string) => {
    if (!window.confirm('Ativar acesso digital e enviar email ao cliente?')) return;
    setActivatingId(orderId);
    try {
      const res = await api.post(`/portal/products/orders/${orderId}/activate-digital`);
      if (res.data?.success) {
        alert(res.data.message || 'Acesso digital ativado com sucesso.');
        loadData();
      } else {
        alert(res.data?.error || 'Erro ao ativar acesso digital.');
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message || 'Erro ao ativar acesso digital.');
    } finally {
      setActivatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pedidos</h1>
          <p className="text-gray-600">Gerir pedidos da sua loja</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Pesquisar por email ou nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadData(); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
          />
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
          >
            <option value="">Todos os estados</option>
            {Object.entries(ESTADOS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <button
            onClick={loadData}
            className="px-3 py-2 bg-pagali-primary text-white rounded-lg text-sm hover:opacity-90"
          >
            Pesquisar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">Fechar</button>
        </div>
      )}

      {/* Repair */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm font-medium text-amber-800 mb-2">Reparar encomenda por Ref. SISP</p>
        <p className="text-xs text-amber-700 mb-3">
          Se uma encomenda foi paga mas não aparece na lista, insira a Ref. Interna do SISP ou o email do cliente.
        </p>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Ref. Interna SISP..."
            value={repairRef}
            onChange={(e) => setRepairRef(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-2 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <button
            onClick={handleRepair}
            disabled={repairing || !repairRef.trim()}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
          >
            {repairing ? 'A corrigir...' : 'Corrigir'}
          </button>
        </div>
        {repairMsg && (
          <p className={`mt-2 text-sm ${repairMsg.includes('Erro') ? 'text-red-600' : 'text-green-700'}`}>
            {repairMsg}
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-semibold">{orders.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Pendentes</p>
          <p className="text-2xl font-semibold text-yellow-600">
            {orders.filter(o => o.ESTADO === 'PENDENTE').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Em Progresso</p>
          <p className="text-2xl font-semibold text-blue-600">
            {orders.filter(o => ['CONFIRMADO', 'EM_PREPARACAO', 'ENVIADO'].includes(o.ESTADO)).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Entregues</p>
          <p className="text-2xl font-semibold text-green-600">
            {orders.filter(o => o.ESTADO === 'ENTREGUE').length}
          </p>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pedido</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Nenhum pedido encontrado.
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const estadoInfo = ESTADOS[order.ESTADO] || { label: order.ESTADO, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={order.ID} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{order.ID?.substring(0, 8)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>{order.CLIENTE_NOME || order.NOME_CLIENTE || '—'}</div>
                        {order.EMAIL_CLIENTE && (
                          <div className="text-xs text-gray-400">{order.EMAIL_CLIENTE}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.TOTAL?.toLocaleString('pt-CV')} ECV
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.DATA_CRIACAO ? new Date(order.DATA_CRIACAO).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${estadoInfo.color}`}>
                          {estadoInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {/* Ativar acesso digital — para pedidos pagos */}
                          {(order.ESTADO === 'PAGO' || order.ESTADO === 'PENDENTE') && (
                            <button
                              onClick={() => handleActivateDigital(order.ID)}
                              disabled={activatingId === order.ID}
                              className="px-2 py-1 text-xs bg-pagali-purple text-white rounded hover:opacity-90 disabled:opacity-50"
                              title="Ativar acesso digital e reenviar email ao cliente"
                            >
                              {activatingId === order.ID ? '...' : 'Digital'}
                            </button>
                          )}
                          {order.ESTADO !== 'ENTREGUE' && order.ESTADO !== 'CANCELADO' && (
                            <select
                              value=""
                              onChange={(e) => {
                                if (e.target.value) handleUpdateStatus(order.ID, e.target.value);
                              }}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="">Alterar estado...</option>
                              {Object.entries(ESTADOS)
                                .filter(([key]) => key !== order.ESTADO)
                                .map(([key, { label }]) => (
                                  <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PortalOrders;
